import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column, DataTableRef } from '@repo/ui/data-table'
import { DeleteIcon, DownloadFile, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { DisplayImage } from '../../../../../../packages/ui/components/atoms/input/file/display-image'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TeflInput } from '../../../lib/types/models/tefl/tefl-input'
import { TeflDiplomaInput } from '../../../lib/types/models/tefl-diploma/tefl-diploma-input'
import { IdInput } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import TeflDiplomaForm from './tefl-diploma-form'

const TeflDiplomaTableForm = () => {
  const form = useFormContext<TeflInput>()
  const diplomas = form.watch('diplomas') || []

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete' | 'image-preview',
    TeflDiplomaInput
  >(['create', 'edit', 'delete', 'image-preview'])

  const ref = useRef<DataTableRef>(null)

  const columns: Column<IdInput<TeflDiplomaInput>>[] = [
    {
      enableSorting: true,
      header: 'Diploma',
      accessorKey: 'name'
    },
    {
      enableSorting: true,
      header: 'Date',
      accessorKey: 'yearOfObtention'
    },

    {
      header: 'Document',
      accessorKey: 'file',
      cell: ({ row }) =>
        !row.original.file ? (
          '-'
        ) : (
          <DisplayImage
            type={row.original.file.type}
            src={row.original.file.path}
            className={'w-12 h-10 rounded-sm hover:cursor-pointer'}
            onImagePreview={() => {
              if (row.original.file.type === FileTypeEnum.IMAGE) {
                toggleModal('image-preview', row.original)
              } else {
                // window.open(row.original.file.path, '_blank')
                toggleModal('image-preview', row.original)
              }
            }}
          />
        )
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => toggleModal('edit', row.original)}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => toggleModal('delete', row.original)}
          />
        </div>
      )
    }
  ]

  return (
    <ActionTableWrapper
      title={'TEFL Diplomas'}
      actions={[
        <Button
          key={'add'}
          type="button"
          icon={PlusIcon}
          label={'Add a TEFL Diploma'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      ]}
    >
      {form.formState.errors.diplomas?.message && !form.watch('diplomas') && (
        <Typography variant="label-medium" className="text-red">
          Please add at least one TEFL Diploma
        </Typography>
      )}

      {diplomas.length > 0 && (
        <DataTable<IdInput<TeflDiplomaInput>>
          ref={ref}
          manualSorting={false}
          columns={columns}
          data={diplomas.map((child, index) => ({ ...child, id: index }))}
          rowCount={diplomas.length}
          paginated={false}
        />
      )}

      <Modal
        isOpen={modalState.create}
        title={'Add a TEFL Diploma'}
        icon={() => <DownloadFile color={tailwindColors.secondary.DEFAULT} />}
        onCancel={() => closeModel('create')}
        size="medium"
        hideActions
      >
        <TeflDiplomaForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            isEnglish: true,
            onCancel: () => closeModel('create'),
            onConfirm: (value) => {
              form.setValue('diplomas', [...diplomas, value])
              closeModel('create')
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.edit}
        title={'Edit TEFL Diploma'}
        icon={() => <DownloadFile color={tailwindColors.secondary.DEFAULT} />}
        onCancel={() => closeModel('edit')}
        size="medium"
        hideActions
      >
        <TeflDiplomaForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: (values) => {
              form.setValue(
                'diplomas',
                diplomas.map((diploma) => (diploma.id === values.id ? values : diploma))
              )
              closeModel('edit')
            }
          })}
          defaultValues={selectedData}
        />
      </Modal>
      {selectedData && (
        <Modal
          isOpen={modalState['image-preview']}
          size="large"
          hideActions
          hideHeader
          onCancel={() => toggleModal('image-preview')}
        >
          <DisplayImage
            type={selectedData.file.type}
            src={selectedData.file.path}
            allowAll
            className={cn('rounded-xl h-fit', {
              'h-[80vh] ':
                selectedData.file.type === FileTypeEnum.PDF ||
                selectedData.file.type === FileTypeEnum.XML
            })}
          />
        </Modal>
      )}

      {selectedData && (
        <Modal
          actionButtonsOptions={{
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: () => {
              form.setValue(
                'diplomas',
                diplomas.filter((_, index) => index !== selectedData.id)
              )

              closeModel('delete')
            }
          }}
          onCancel={() => closeModel('delete')}
          isOpen={modalState.delete}
          title={`Are you sure you want to delete the diploma "${selectedData.name}"?`}
          icon={DeleteIcon}
          variant={'destructive'}
        />
      )}
    </ActionTableWrapper>
  )
}

export default TeflDiplomaTableForm
